import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Check, Shield, BarChart3, Megaphone, Globe } from 'lucide-react';

const COOKIE_CATEGORIES = [
  {
    id: 'necessary',
    name: 'Notwendig',
    description: 'Auth-Cookie, CSRF-Schutz, Session-Verwaltung',
    icon: Shield,
    required: true,
  },
  {
    id: 'statistics',
    name: 'Statistik',
    description: 'Anonyme Nutzungsstatistiken zur Verbesserung der Website',
    icon: BarChart3,
    required: false,
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Personalisierte Werbung und Remarketing',
    icon: Megaphone,
    required: false,
  },
  {
    id: 'external',
    name: 'Externe Dienste',
    description: 'Telegram-Benachrichtigungen, E-Mail-Tracking',
    icon: Globe,
    required: false,
  },
];

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    statistics: false,
    marketing: false,
    external: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('farbwelt_cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    const consent = {
      ...prefs,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    localStorage.setItem('farbwelt_cookie_consent', JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
    
    // Trigger consent event for scripts that need to wait for consent
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consent }));
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      statistics: true,
      marketing: true,
      external: true,
    });
  };

  const rejectAll = () => {
    saveConsent({
      necessary: true,
      statistics: false,
      marketing: false,
      external: false,
    });
  };

  const saveSelected = () => {
    saveConsent(preferences);
  };

  const toggleCategory = (id) => {
    if (id === 'necessary') return; // Cannot toggle necessary
    setPreferences(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      {!showSettings && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-[9999] p-3 sm:p-4 pointer-events-none" 
          data-testid="cookie-banner"
        >
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg border border-gray-200 pointer-events-auto">
            <div className="p-4">
              {/* Header with Icon */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                  <Cookie className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">Cookie-Einstellungen</h3>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Wir nutzen Cookies für den Betrieb und zur Verbesserung unserer Website.{' '}
                    <Link to="/datenschutz" className="text-[#9B1B30] hover:underline">
                      Datenschutzerklärung
                    </Link>
                  </p>
                </div>
              </div>
              
              {/* Three Equal Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={rejectAll}
                  type="button"
                  className="flex-1 px-3 py-3 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation select-none"
                  data-testid="cookie-reject-btn"
                >
                  Ablehnen
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  type="button"
                  className="flex-1 px-3 py-3 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation select-none"
                  data-testid="cookie-settings-btn"
                >
                  Einstellungen
                </button>
                <button
                  onClick={acceptAll}
                  type="button"
                  className="flex-1 px-3 py-3 text-sm font-medium border border-[#9B1B30] bg-[#9B1B30] text-white rounded-lg hover:bg-[#7A1525] active:bg-[#7A1525] transition-colors touch-manipulation select-none"
                  data-testid="cookie-accept-btn"
                >
                  Akzeptieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-3 sm:p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/40" 
            onClick={() => setShowSettings(false)}
          />
          
          {/* Modal */}
          <div 
            className="relative w-full max-w-md bg-white rounded-xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
            data-testid="cookie-settings-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">Cookie-Einstellungen</h3>
              </div>
              <button 
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Categories */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {COOKIE_CATEGORIES.map((category) => {
                const Icon = category.icon;
                const isActive = preferences[category.id];
                
                return (
                  <div 
                    key={category.id}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                      <Icon className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900">{category.name}</span>
                        {category.required && (
                          <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                            Erforderlich
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                        {category.description}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleCategory(category.id)}
                      disabled={category.required}
                      className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${
                        isActive 
                          ? 'bg-[#9B1B30] justify-end' 
                          : 'bg-gray-300 justify-start'
                      } ${category.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`cookie-toggle-${category.id}`}
                    >
                      <div className="w-5 h-5 bg-white rounded-full shadow" />
                    </button>
                  </div>
                );
              })}
              
              {/* Info Text */}
              <p className="text-xs text-gray-400 pt-2">
                Notwendige Cookies können nicht deaktiviert werden, da sie für den sicheren Betrieb erforderlich sind.{' '}
                <Link to="/datenschutz" className="text-[#9B1B30] hover:underline">
                  Mehr erfahren
                </Link>
              </p>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex gap-2">
              <button
                onClick={rejectAll}
                className="flex-1 px-3 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Alle ablehnen
              </button>
              <button
                onClick={saveSelected}
                className="flex-1 px-3 py-2.5 text-sm font-medium bg-[#9B1B30] text-white rounded-lg hover:bg-[#7A1525] transition-colors flex items-center justify-center gap-1"
                data-testid="cookie-save-btn"
              >
                <Check className="w-4 h-4" />
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Helper function to check consent (can be imported by other components)
export function getCookieConsent() {
  try {
    const consent = localStorage.getItem('farbwelt_cookie_consent');
    return consent ? JSON.parse(consent) : null;
  } catch {
    return null;
  }
}

export function hasConsent(category) {
  const consent = getCookieConsent();
  if (!consent) return false;
  return consent[category] === true;
}
